import _styled from "styled-components";
import React, { useCallback, useMemo } from 'react';
import differenceInDays from 'date-fns/differenceInDays';
import { useTranslation } from 'react-i18next';
import { EmployerCandidateCard, EmployerCandidateCardHeader, EmployerCandidateCardInfo, EmployerCandidatesCardLabels, EmployerCandidateRowCard, EmployerCandidateRowInfo, EmployerCandidateRowDistance, EmployerCandidateRowLabels, EmployerCandidateUnsuitabilityReason // EmployerCandidateRowContact,
} from './employer-candidate-card';
import { useResponsiveBreakPoints } from '@restworld/utils-common';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function EmployerCandidateKanbanCard(_ref) {
  var candidate = _ref.candidate,
      ctaButton = _ref.ctaButton,
      distance = _ref.distance,
      labels = _ref.labels,
      unsuitabilityReason = _ref.unsuitabilityReason,
      lastStatusChangeDate = _ref.lastStatusChangeDate,
      wrapperStyle = _ref.wrapperStyle,
      statusKey = _ref.statusKey,
      section = _ref.section,
      footerSlot = _ref.footerSlot;

  var _useCandidateLastChan = useCandidateLastChangeBadgeInfo(),
      getCandidateLastChangeBadgeInfo = _useCandidateLastChan.getCandidateLastChangeBadgeInfo;

  var _useResponsiveBreakPo = useResponsiveBreakPoints(),
      isDesktop = _useResponsiveBreakPo.isDesktop;

  var _useMemo = useMemo(function () {
    return getCandidateLastChangeBadgeInfo(lastStatusChangeDate, statusKey);
  }, [getCandidateLastChangeBadgeInfo, lastStatusChangeDate, statusKey]),
      badgeVariant = _useMemo.badgeVariant,
      badgeText = _useMemo.badgeText;

  var badge = useMemo(function () {
    return {
      variant: badgeVariant,
      text: badgeText
    };
  }, [badgeText, badgeVariant]);
  var currrentCta = useMemo(function () {
    return {
      label: ctaButton.label,
      onClick: ctaButton.onClick,
      variant: ctaButton.variant
    };
  }, [ctaButton.label, ctaButton.onClick, ctaButton.variant]);

  if (isDesktop && section === 'shortlisted') {
    return /*#__PURE__*/_jsxs(EmployerCandidateRowCard, {
      ctaButton: currrentCta,
      twStyle: wrapperStyle,
      footerSlot: footerSlot && /*#__PURE__*/_jsx(RowFooterSlot, {
        children: footerSlot
      }),
      children: [/*#__PURE__*/_jsx(EmployerCandidateRowInfo, {
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        badge: badge,
        showFire: candidate === null || candidate === void 0 ? void 0 : candidate.isRecommended,
        candidateImage: candidate === null || candidate === void 0 ? void 0 : candidate.image
      }), /*#__PURE__*/_jsx(EmployerCandidateRowDistance, {
        distance: distance
      }), unsuitabilityReason ? /*#__PURE__*/_jsx(EmployerCandidateUnsuitabilityReason, {
        reason: unsuitabilityReason,
        row: true
      }) : /*#__PURE__*/_jsx(EmployerCandidateRowLabels, {
        labels: labels === null || labels === void 0 ? void 0 : labels.values
      })]
    });
  }

  return /*#__PURE__*/_jsxs(EmployerCandidateCard, {
    ctaButton: currrentCta,
    twStyle: wrapperStyle,
    newShortlistedSection: section === 'shortlisted',
    children: [/*#__PURE__*/_jsx(EmployerCandidateCardHeader, {
      firstName: candidate.firstName,
      lastName: candidate.lastName
    }), /*#__PURE__*/_jsx(EmployerCandidateCardInfo, {
      candidateImage: candidate.image,
      firstName: candidate.firstName,
      lastName: candidate.lastName,
      badge: badge,
      distance: distance,
      showFire: candidate === null || candidate === void 0 ? void 0 : candidate.isRecommended
    }), unsuitabilityReason ? /*#__PURE__*/_jsx(EmployerCandidateUnsuitabilityReason, {
      reason: unsuitabilityReason
    }) : /*#__PURE__*/_jsx(EmployerCandidatesCardLabels, {
      labels: labels.values,
      variant: "neutral"
    }), footerSlot && /*#__PURE__*/_jsx(ColumnFooterSlot, {
      children: footerSlot
    })]
  });
}
export default EmployerCandidateKanbanCard; // Span the full row of the desktop grid (`grid-cols-9`) so the footer renders
// as a new row beneath name/distance/labels instead of squeezing into a cell.

var RowFooterSlot = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-candidate-cardkanban__RowFooterSlot",
  componentId: "sc-176pinb-0"
})(["", ""], {
  "gridColumn": "span 9 / span 9",
  "marginTop": "0.75rem",
  "borderTopWidth": "1px",
  "--tw-border-opacity": "1",
  "borderColor": "rgb(222 222 225 / var(--tw-border-opacity))",
  "paddingTop": "0.75rem"
}); // Column variant (mobile): container is already flex-col with gap, so the
// footer naturally sits on its own row beneath the labels — no divider needed.


var ColumnFooterSlot = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-candidate-cardkanban__ColumnFooterSlot",
  componentId: "sc-176pinb-1"
})([""]);

export function useCandidateLastChangeBadgeInfo() {
  var _useTranslation = useTranslation('common'),
      translateCommon = _useTranslation.t;

  var getCandidateLastChangeBadgeInfo = useCallback(function (lastStatusChangeDate, statusKey) {
    var daysSinceChange = differenceInDays(new Date(), new Date(lastStatusChangeDate));
    var badgeVariant;
    var badgeText;

    if (statusKey === 'worker_jp_in_shortlist') {
      badgeVariant = 'fresh';
      badgeText = translateCommon('new');
    } else if (daysSinceChange === 0) {
      badgeVariant = 'success';
      badgeText = translateCommon('today');
    } else {
      badgeVariant = daysSinceChange <= 3 ? 'warning' : 'danger';
      badgeText = daysSinceChange > 1 ? "".concat(daysSinceChange, " ").concat(translateCommon('days_ago')) : "".concat(daysSinceChange, " ").concat(translateCommon('day_ago'));
    }

    return {
      badgeVariant: badgeVariant,
      badgeText: badgeText
    };
  }, [translateCommon]);
  return {
    getCandidateLastChangeBadgeInfo: getCandidateLastChangeBadgeInfo
  };
}